import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { cookieUtils } from '@/utils/cookieUtils';
import { useAuth } from '@/hooks/useAuth';
import { client } from '@/utils/apiClients';
import { useTheme } from '@/hooks/useTheme';

const TokenAuthLoader = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { setToken } = useAuth();
    const { setTheme } = useTheme();
    const [isAuthenticating, setIsAuthenticating] = useState(true);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const urlToken = searchParams.get('token');
    const urlTheme = searchParams.get('theme');
    const latitude = searchParams.get('latitude');
    const longitude = searchParams.get('longitude');

    useEffect(() => {
        // Apply theme from URL parameter if provided
        const normalizedTheme = urlTheme === 'white' ? 'light' : urlTheme;
        if (normalizedTheme === 'dark' || normalizedTheme === 'light') {
            setTheme(normalizedTheme as 'dark' | 'light');
        }

        const authenticateToken = async () => {
            try {

                if (urlToken) {
                    setToken(urlToken);

                    try {

                        cookieUtils.setAuthToken(urlToken);
                        const profileResponse = await client('/profile', { method: 'GET', token: urlToken });


                        if (profileResponse && profileResponse.success) {

                            if (latitude && longitude) {
                                cookieUtils.setUserLocation(latitude, longitude);
                            }

                            // await new Promise(resolve => setTimeout(resolve, 1000));
                            navigate('/forms', { replace: true });
                        } else {


                            cookieUtils.clearAllAuthCookies();
                            setErrorMessage('Failed to fetch user profile. Please try again.');
                            setIsAuthenticating(false);
                            navigate('/invalid-authorization', { replace: true });
                        }
                    } catch (profileError: any) {


                        cookieUtils.clearAllAuthCookies();
                        setErrorMessage(profileError?.message || 'Invalid token or unable to fetch profile.');
                        setIsAuthenticating(false);
                        navigate('/invalid-authorization', { replace: true });
                    }
                } else {
                    //                     console.log("fdjgjdfb");

                    //                     // No URL token, check for existing cookie token
                    //                     const cookieToken = cookieUtils.getAuthToken();
                    // console.log("cookieToken:", cookieToken);

                    //                     if (cookieToken) {
                    //                         console.log("cookieToken:", cookieToken);
                    //                         navigate('/forms', { replace: true });
                    //                     } else {
                    //                           console.log("urlToken:", urlToken);
                    setIsAuthenticating(false);
                    navigate('/invalid-authorization', { replace: true });
                    // }
                }
            } catch (error) {
                console.error("Authentication error:", error);
                setErrorMessage('An unexpected error occurred during authentication.');
                setIsAuthenticating(false);
                navigate('/invalid-authorization', { replace: true });
            }
        };

        authenticateToken();
    }, [JSON.stringify(urlToken), urlTheme, navigate, setTheme]);

    if (!isAuthenticating) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-950 px-4">
                <div className="max-w-md w-full bg-white dark:bg-slate-900 rounded-lg shadow-md p-8 text-center">
                    <div className="flex justify-center mb-6">
                        <svg
                            className="w-16 h-16 text-red-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                            />
                        </svg>
                    </div>

                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                        Authentication Required
                    </h1>

                    <p className="text-gray-600 dark:text-gray-300 mb-6">
                        {errorMessage || 'You need to be authenticated to access this page. Please log in to continue.'}
                    </p>

                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-6">
                        If you believe this is an error, please contact support.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-950 px-4">
            <div className="max-w-md w-full bg-white dark:bg-slate-900 rounded-lg shadow-md p-8 text-center">
                <div className="flex justify-center mb-6">
                    <svg
                        className="animate-spin h-16 w-16 text-blue-600"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                    >
                        <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                        />
                        <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                    </svg>
                </div>

                <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                    Authenticating...
                </h1>

                <p className="text-gray-600 dark:text-gray-300">
                    Please wait while we verify your credentials
                </p>
            </div>
        </div>
    );
};

export default TokenAuthLoader;
